/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmailCaptureForm } from "@/components/landing/email-capture-form";
import { CheckCircle, Sparkles } from "lucide-react";
import { Suspense, useCallback, useEffect, useState } from "react";

const perks = [
  "Early access pricing locked in",
  "Priority onboarding & setup help",
  "Shape the product with your feedback",
];

export function WaitlistDialog() {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);

  useEffect(() => {
    window.addEventListener("open-waitlist", handleOpen);
    return () => window.removeEventListener("open-waitlist", handleOpen);
  }, [handleOpen]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("waitlist") === "open") {
      setOpen(true);
      params.delete("waitlist");
      const cleanUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", cleanUrl);
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        size="md"
        className="rounded-2xl border border-border bg-card shadow-2xl p-0 overflow-hidden"
      >
        {/* Header with accent background */}
        <div className="bg-primary px-6 pt-6 pb-5">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-secondary" />
              <DialogTitle className="text-primary-foreground text-xl font-bold">
                Join the Waitlist
              </DialogTitle>
            </div>
            <DialogDescription className="text-primary-foreground/70 text-sm">
              Be first in line when we launch. Early members get exclusive
              pricing.
            </DialogDescription>
          </DialogHeader>

          {/* Perks */}
          <ul className="mt-4 space-y-2">
            {perks.map((perk) => (
              <li
                key={perk}
                className="flex items-center gap-2 text-sm text-primary-foreground/80"
              >
                <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Form area */}
        <div className="px-6 pb-6 pt-4">
          <Suspense fallback={<div className="h-12" />}>
            <EmailCaptureForm
              buttonText="Join the Waitlist"
              successMessage="You're on the list! We'll notify you when we launch."
              variant="stacked"
              showExtraFields
            />
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  );
}
